import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';

import cls from 'classnames';
import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';

import { analytics, getCutDetail } from '@/utils/analytics';

function CutsNavigation({
  assets = [],
  entries = [],
  cuts = [],
  onChange,
  backgroundSrc,
  svgRef,
}) {
  const isWH = isWHEnv();
  const isDD = isDDEnv();

  const labels = {};
  const { locale } = useRouter();
  const isUs = locale === 'en-US';

  cuts = cuts.map((c) => getContent(c.sys.id, entries));
  cuts
    .filter((c) => c && c.fields)
    .forEach((cut) => {
      labels[cut.fields.slug] = cut.fields.name;
    });

  const activateCut = useCallback(
    (el) => {
      const paths = el.querySelectorAll('path');
      const text = el.querySelector('text');

      el.classList.add('active');
      paths.forEach((path) => {
        path.classList.remove('fill-white');
        path.classList.add('fill-eucalyptus');
        path.classList.add('stroke-eucalyptus');
        path.style.fillOpacity = '1';
        path.setAttribute('opacity', '1');
      });
      text.setAttribute('opacity', '1');

      if (onChange) {
        onChange(el.dataset.slug);
      }
    },
    [onChange]
  );

  useEffect(() => {
    const ref = svgRef.current;
    ref.querySelectorAll('#outlines > g').forEach(function (el) {
      const paths = el.querySelectorAll('path');
      const text = el.querySelector('text');
      paths.forEach((p) => p.classList.add('fill-white'));

      el.addEventListener('mouseover', function (e) {
        e.stopPropagation();
        if (!el.classList.contains('active')) {
          paths.forEach((p) => (p.style.fillOpacity = '0.9'));
        }
      });

      el.addEventListener('mouseout', function (e) {
        e.stopPropagation();
        if (!el.classList.contains('active')) {
          paths.forEach((p) => (p.style.fillOpacity = '0'));
        }
      });

      el.addEventListener('click', function (e) {
        e.stopPropagation();

        const activeCut = svgRef.current.querySelector('#outlines > g.active');
        const paths = activeCut.querySelectorAll('path');
        const text = activeCut.querySelector('text');

        activeCut.classList.remove('active');
        paths.forEach((path) => {
          path.classList.add('fill-white');
          path.classList.remove('stroke-eucalyptus');
          path.classList.remove('fill-eucalyptus');
          path.style.fillOpacity = '0';
          path.setAttribute('opacity', '0.9');
        });
        text.setAttribute('opacity', '0.9');

        analytics({
          window,
          document,
          detail: getCutDetail(el, {
            cutPart: el?.querySelector('text')?.textContent,
          }),
        });

        activateCut(el);
      });
    });

    //set default selection to chuck
    if (svgRef.current.querySelector('#outlines > g.active') === null) {
      activateCut(svgRef.current.querySelector('[data-slug=chuck]'));
    }

    return () => {
      ref.querySelectorAll('#outlines > g').forEach((e) => {
        e.removeEventListener('mouseover');
        e.removeEventListener('mouseout');
        e.removeEventListener('click');
      });
    };
  }, [activateCut, svgRef]);

  return (
    <svg
      ref={svgRef}
      className={cls('cuts-outline w-[95%] md:w-auto h-fit md:h-[92%]', {
        'md:absolute md:top-[6%]': !isWH,
        'h-[60%] md:h-[70%] m-auto': isWH,
      })}
      viewBox="0 0 598 370"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.402832"
        width="597.193"
        height="370"
        fill="url(#pattern0)"
        style={{ mixBlendMode: 'multiply' }}
      />
      <g id="cow">
        <g id="outlines">
          <g data-slug="tongue">
            <path
              d="M18.5017 99.1876C10.3617 101.602 16.96 105.724 24.6684 104.309C33.5639 102.679 41.8118 95.435 50.8769 94.0816C59.9419 92.7282 65.3378 100.849 68.5136 94.0816C71.6895 87.3146 60.8053 76.1799 48.1635 79.0405C35.5218 81.9011 42.3668 92.0669 18.5017 99.1876Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="tongue_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="26.2988" y="117.785">
                Tongue
              </tspan>
            </text>
          </g>
          <g data-slug="cheek">
            <path
              d="M100.55 58.293C91.2996 60.077 87.3684 64.3526 84.3621 70.8581C81.5562 76.9176 76.3762 87.7755 76.3916 95.4652C76.3916 100.417 78.7812 112.275 91.315 103.063C104.774 93.189 121.779 89.0827 120.607 72.6421C119.697 60.0463 109.815 56.4936 100.55 58.293Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="cheek_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="87.1133" y="118.785">
                Cheek
              </tspan>
            </text>
          </g>
          <g data-slug="shin">
            <path
              d="M274.897 225.021C274.47 231.442 273.657 237.831 272.461 244.154C270.565 252.828 268.684 257.195 268.684 271.837H245.605C245.605 271.837 247.964 240.801 245.605 229.389C255.442 228.467 265.221 227.009 274.897 225.021V225.021Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <path
              d="M238.884 229.881C238.884 229.881 225.41 232.572 216.499 231.742C218.179 241.139 220.199 271.836 220.199 271.836H237.512C237.512 271.836 242.091 238.124 238.884 229.881Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="shin_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="279.417" y="256.785">
                {isUs ? 'Shank' : 'Shin'}
              </tspan>
            </text>
          </g>
          <g data-slug="shortloin">
            <path
              d="M394.408 106.354C393.906 85.4474 391.845 64.607 388.241 44.0057C371.082 44.221 352.367 43.6674 339.093 42.6677C331.539 42.0986 321.98 40.8991 311.836 39.5918C316.723 64.3301 319.497 89.4374 320.13 114.644C338.522 113.183 371.422 110.337 394.408 106.354Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="shortloin_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              {isUs ? (
                <tspan x="340.417" y="85.785">
                  Loin
                </tspan>
              ) : (
                <tspan x="325.417" y="85.785">
                  Shortloin
                </tspan>
              )}
            </text>
          </g>
          <g data-slug="oxtail">
            <path
              d="M535.703 33.0095C530.862 25.3198 525.096 19.168 518.251 16.6765C512.254 14.4465 504.098 16.6765 494.278 19.9523C505.871 24.0894 521.411 29.1646 535.703 33.0095Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="oxtail_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="541.206" y="27.7852">
                {isUs ? 'Oxtail' : 'Tail'}
              </tspan>
            </text>
          </g>
          <g data-slug="round">
            <path
              d="M517.773 79.4249C500.367 79.3019 478.429 83.3006 469.534 85.0692C471.774 99.0281 473.287 113.094 474.066 127.209C498.09 122.816 522.442 120.455 546.864 120.15C546.864 119.658 547.003 119.212 547.049 118.842C548.212 105.393 548.629 91.8898 548.298 78.3945C538.662 78.9174 527.501 79.4711 517.773 79.4249Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="round_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="491.012" y="104.7852">
                Round
              </tspan>
            </text>
          </g>
          <g data-slug="knuckle">
            <path
              d="M474.436 134.959C474.914 147.555 474.853 160.92 474.036 174.946C477.667 172.963 481.464 171.295 485.382 169.963C513.765 160.628 542.748 160.735 542.748 160.735C542.748 160.735 544.613 140.65 545.986 127.885C521.98 128.256 498.048 130.622 474.436 134.959V134.959Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="round_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="485.329" y="151.785">
                Knuckle
              </tspan>
            </text>
          </g>
          <g data-slug="flank">
            <path
              d="M466.774 136.344C447.287 139.989 428.001 144.372 408.977 148.724C377.696 155.86 348.004 162.566 319.251 165.642C317.91 186.819 315.659 203.875 314.041 214.333C339 216.548 377.249 222.699 404.275 216.44C427.585 211.026 446.409 192.371 466.019 179.698C467.026 165.27 467.279 150.799 466.774 136.344V136.344Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="flank_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="378.204" y="188.785">
                Flank
              </tspan>
            </text>
          </g>
          <g data-slug="chuck">
            <path
              d="M261.362 104.478C261.238 80.6705 260.837 55.0176 259.912 34.5629C255.032 33.8559 250.106 33.5064 245.174 33.5171C225.456 32.8097 218.657 36.9467 206.833 36.593C195.008 36.2393 166.98 34.7936 166.98 34.7936C166.98 34.7936 162.355 84.5 132.925 105.247V124.933C155.788 123.687 213.4 118.765 261.362 104.478Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="chuck_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="189.011" y="85.7852">
                Chuck
              </tspan>
            </text>
          </g>
          <g data-slug="blade">
            <path
              d="M188.379 128.131C167.736 130.376 149.451 131.668 138.151 132.345C143.238 137.159 150.808 145.279 154.847 154.245C155.032 154.66 155.233 155.106 155.433 155.583C178.774 157.752 212.598 161.612 261.284 160.489C261.377 148.847 261.469 131.545 261.408 112.475C239.007 118.903 224.808 124.178 188.379 128.131Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="blade_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="194.011" y="150.852">
                {isUs ? 'Clod' : 'Blade'}
              </tspan>
            </text>
          </g>
          <g data-slug="rump">
            <path
              d="M449.877 34.7012C440.935 37.9964 431.7 40.4423 422.297 42.0064C413.605 43.1427 404.854 43.7794 396.088 43.9134C401.578 76.4952 403.44 109.583 401.638 142.573L407.281 141.281C426.752 136.851 446.486 132.345 466.466 128.654C464.906 96.7667 459.333 65.2015 449.877 34.7012V34.7012Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="rump_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="413.011" y="95.852">
                {isUs ? 'Sirloin' : 'Rump'}
              </tspan>
            </text>
          </g>
          <g data-slug="shin">
            <path
              d="M502.464 176.501C502.464 176.501 531.139 170.949 541.576 172.686C540.034 187.451 537.599 209.597 548.375 231.128H524.818C518.96 217.748 520.316 200.708 502.464 176.501Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <path
              d="M484.334 182.205C484.334 182.205 474.112 186.142 469.318 192.232C471.8 213.487 482.854 234.003 482.854 234.003H509.848C509.848 234.003 499.982 212.718 500.228 194.801C493.214 186.834 490.531 185.342 484.334 182.205Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="knee_caps_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="411.139" y="251.785">
                {isUs ? 'Shank' : 'Shin'}
              </tspan>
            </text>
          </g>
          <g data-slug="brisket">
            <path
              d="M311.497 166.395H311.281C294.246 167.779 276.316 168.302 258.756 168.302C218.673 168.302 180.516 165.518 158.887 163.596C163.373 173.931 170.372 190.725 183.214 199.43C198.122 209.534 198.184 222.976 216.499 222.96C254.378 222.96 274.05 212.564 304.251 213.733L306.332 213.825C307.905 203.782 310.14 187.172 311.497 166.395Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="brisket_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="230.467" y="198.785">
                Brisket
              </tspan>
            </text>
          </g>
          <g data-slug="ribset">
            <path
              d="M303.758 38.4082C296.835 37.4393 274.389 36.5011 267.667 35.5938C269.394 75.1805 269.209 132.915 269.008 160.275C277.873 159.967 302.093 159.444 310.633 158.737L311.928 158.614C313.717 124.795 312.9 81.6245 303.758 38.4082Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="ribset_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              {isUs ? (
                <tspan x="280.436" y="102.785">
                  Rib
                </tspan>
              ) : (
                <tspan x="270.436" y="102.785">
                  Rib Set
                </tspan>
              )}
            </text>
          </g>
          <g data-slug="skirt">
            <path
              d="M320.33 122.288C320.515 134.899 320.238 146.895 319.698 157.876C343.486 155.277 368.107 150.079 393.776 144.28C394.223 137.067 394.654 126.671 394.546 114.137C370.897 118.151 338.399 120.934 320.33 122.288Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="skirt_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="345.436" y="140.785">
                Skirt
              </tspan>
            </text>
          </g>
          <g data-slug="topside">
            <path
              d="M517.803 71.7339C527.439 71.8108 538.493 71.3187 548.02 70.7189C547.619 63.3521 546.879 57.1081 545.615 53.8015C544.074 49.8643 542.532 45.9733 540.728 42.2822C524.14 38.2067 505.979 32.1933 498.502 29.6403C491.687 27.318 486.584 25.4879 482.73 23.9961C479.493 25.1188 476.117 26.2107 472.617 27.2411C466.913 28.9021 461.825 30.5939 457.2 32.2395C461.873 47.083 465.581 62.212 468.3 77.532C477.535 75.6249 499.596 71.6416 517.803 71.7339Z"
              opacity="0.4"
              stroke="white"
              strokeMiterlimit="10"
            />
            <text
              id="topside_text"
              opacity="0.9"
              fill="white"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="NB International Pro"
              fontSize="14" fontWeight="600"
              letterSpacing="0.14px"
            >
              <tspan x="478.436" y="56.785">
                Topside
              </tspan>
            </text>
          </g>
        </g>
      </g>
      <defs>
        <pattern
          id="pattern0"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use
            href="#image0_2819_129713"
            transform="translate(-0.00472666) scale(0.000625824 0.0010101)"
          />
        </pattern>
        <image
          id="image0_2819_129713"
          width="1613"
          height="990"
          href={`${backgroundSrc}?fm=webp&q=80&w=800`}
        />
      </defs>
    </svg>
  );
}

// @TODO: define real props for the Header based on it requirements
CutsNavigation.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  cuts: PropTypes.array,
};

export default CutsNavigation;
